// Свободный канвас пространства по образцу прототипа Claude Design.
// Доски расставляются абсолютно по координатам {cx, cy} (cell-coords, CELL=8px).
// Drag за header доски → snap к 8px-сетке. Сетка видна только при drag.
// Pan: Space+drag, middle-mouse, scroll. Zoom: Ctrl+wheel.
// 4 пресета фона: warm-dawn, cool-mist, paper, plain.

import { useState, useRef, useEffect } from 'react';
import { Plus, Settings2, LayoutDashboard, List, AlignVerticalJustifyStart } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import { Board } from './Board';
import { NewBoardModal } from './NewBoardModal';
import { SpaceListView } from './SpaceListView';
import { useTheme } from '../lib/theme';
import { logAction } from '../lib/actionLog';
import type { BoardRow } from '../types';

type SpaceView = 'canvas' | 'list';

const CELL = 8;
// Канвас имеет фиксированные размеры 5000×5000 px (см. <div> ниже).
// MAX_POS_CELLS — макс координата чтобы доска не уехала за пределы.
// Минус 1 cell на запас, чтобы рамка не торчала за край.
const CANVAS_SIZE_CELLS = Math.floor(5000 / CELL) - 1; // 624

const BACKGROUNDS: Record<string, { label: string; light: string; dark: string }> = {
  'warm-dawn': {
    label: 'Тёплый рассвет',
    light:
      'radial-gradient(1200px 800px at 15% 10%, #fff5ec 0%, transparent 55%), radial-gradient(1000px 700px at 85% 90%, #f3edff 0%, transparent 60%), linear-gradient(180deg, #f1ece4 0%, #e9e3d8 100%)',
    dark:
      'radial-gradient(1200px 800px at 15% 10%, #1a1614 0%, transparent 55%), radial-gradient(1000px 700px at 85% 90%, #16161f 0%, transparent 60%), linear-gradient(180deg, #0a0b0d 0%, #07080a 100%)',
  },
  'cool-mist': {
    label: 'Прохладный туман',
    light:
      'radial-gradient(1200px 800px at 80% 15%, #e3edfa 0%, transparent 60%), radial-gradient(900px 700px at 10% 85%, #e3f5ee 0%, transparent 60%), linear-gradient(180deg, #ecf0f6 0%, #e4e9f1 100%)',
    dark:
      'radial-gradient(1200px 800px at 80% 15%, #0e1620 0%, transparent 60%), radial-gradient(900px 700px at 10% 85%, #0d1815 0%, transparent 60%), linear-gradient(180deg, #08090c 0%, #06070a 100%)',
  },
  paper: {
    label: 'Бумага',
    light: 'linear-gradient(180deg, #ede8df 0%, #e5dfd2 100%)',
    dark: 'linear-gradient(180deg, #0a0b0d 0%, #07080a 100%)',
  },
  mono: {
    label: 'Моно',
    light: '#fafaf9',
    dark: '#0a0a0a',
  },
  'night-sky': {
    label: 'Ночное небо',
    light:
      'radial-gradient(800px 500px at 50% 0%, #d6e0f5 0%, transparent 60%), linear-gradient(180deg, #e8edf5 0%, #d8dde9 100%)',
    dark:
      'radial-gradient(800px 500px at 50% 0%, #1c2333 0%, transparent 60%), linear-gradient(180deg, #07090f 0%, #050608 100%)',
  },
  plain: {
    label: 'Сплошной',
    light: '#e8e4dc',
    dark: '#08090b',
  },
};

interface SpaceCanvasProps {
  spaceId: string;
}

export function SpaceCanvas({ spaceId }: SpaceCanvasProps) {
  // refetchOnMount + refetchOnWindowFocus: всегда подтягиваем свежие позиции
  // досок при возврате во вкладку/повторном входе в пространство. Иначе
  // server-side изменения (auto-reflow при collapse, прямые SQL правки)
  // не виды у клиента до полного reload.
  const spaceQuery = api.spaces.getWithBoards.useQuery(
    { spaceId },
    { refetchOnMount: 'always', refetchOnWindowFocus: true },
  );
  const utils = api.useUtils();
  // updatePos не нужен onSuccess invalidate — данные уже в кеше через optimistic setData.
  // Принудительный invalidate приводил к "дрожанию" (refetch перезаписывал свежий optimistic).
  const updatePos = api.boards.updatePosition.useMutation();
  const updateTweaks = api.spaces.updateTweaks.useMutation({
    onSuccess: () => utils.spaces.getWithBoards.invalidate({ spaceId }),
  });
  const bulkUpdatePositions = api.boards.bulkUpdatePositions.useMutation({
    onSuccess: () => utils.spaces.getWithBoards.invalidate({ spaceId }),
  });
  const handleAutoLayout = () => {
    if (!spaceQuery.data) return;
    if (bulkUpdatePositions.isPending) return; // защита от double-click
    const sorted = [...spaceQuery.data.boards].sort((a, b) => a.canvasY - b.canvasY);
    const positions: Array<{ id: string; canvasX: number; canvasY: number }> = [];
    let cursorY = 1;
    for (const b of sorted) {
      positions.push({ id: b.id, canvasX: 1, canvasY: cursorY });
      // Используем canvasH из БД, НЕ DOM-измерение. canvasH — persisted
      // source of truth: даже если сейчас доска показывается короче (мало
      // карточек), при expand/добавлении она вырастет до canvasH.
      // Если бы брали DOM-h — соседи стояли близко, но при росте доски
      // получался overlap. Зазор 1 cell (8px) — компактная полоска.
      const heightCells = b.collapsed ? 4 : b.canvasH;
      cursorY += heightCells + 1;
    }
    // Снапшот для rollback
    const prevData = utils.spaces.getWithBoards.getData({ spaceId });
    utils.spaces.getWithBoards.setData({ spaceId }, (old) => {
      if (!old) return old;
      const byId = new Map(positions.map((p) => [p.id, p]));
      return {
        ...old,
        boards: old.boards.map((b) => {
          const p = byId.get(b.id);
          return p ? { ...b, canvasX: p.canvasX, canvasY: p.canvasY } : b;
        }),
      };
    });
    bulkUpdatePositions.mutate(
      { positions },
      {
        onError: () => {
          // Откат на исходные позиции если server failed
          if (prevData) utils.spaces.getWithBoards.setData({ spaceId }, prevData);
        },
      },
    );
  };

  const [showNewBoardModal, setShowNewBoardModal] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [view, setView] = useState<SpaceView>('canvas');

  if (!spaceQuery.data) return <div className="p-8 text-sm text-ink-faint_l">Загрузка пространства…</div>;

  const { space, boards } = spaceQuery.data;
  const bgKey = space.background ?? 'cool-mist';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <header className="flex items-center gap-3 px-6 py-3 border-b border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark z-10">
        <h1 className="text-[15px] font-semibold flex-1">{space.title}</h1>

        <div
          className="inline-flex items-center rounded-md bg-surface2-light dark:bg-surface2-dark p-0.5"
          role="tablist"
          aria-label="Режим отображения"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'canvas'}
            onClick={() => setView('canvas')}
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded transition',
              view === 'canvas'
                ? 'bg-surface-light dark:bg-surface-dark shadow-sm font-medium text-ink-light dark:text-ink-dark'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark',
            )}
            title="Канвас с досками"
          >
            <LayoutDashboard size={13} />
            Доска
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            onClick={() => setView('list')}
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded transition',
              view === 'list'
                ? 'bg-surface-light dark:bg-surface-dark shadow-sm font-medium text-ink-light dark:text-ink-dark'
                : 'text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark',
            )}
            title="Все карточки списком"
          >
            <List size={13} />
            Список
          </button>
        </div>

        {view === 'canvas' && (
          <div className="flex items-center gap-1 text-xs text-ink-muted_l dark:text-ink-muted_d">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))}
              className="px-2 py-1 hover:bg-surface2-light dark:hover:bg-surface2-dark rounded"
            >
              −
            </button>
            <span className="w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}
              className="px-2 py-1 hover:bg-surface2-light dark:hover:bg-surface2-dark rounded"
            >
              +
            </button>
          </div>
        )}

        {view === 'canvas' && (
          <>
            <button
              type="button"
              onClick={handleAutoLayout}
              disabled={bulkUpdatePositions.isPending}
              className="inline-flex items-center gap-1 px-2 h-8 rounded text-xs text-ink-faint_l dark:text-ink-faint_d hover:bg-surface2-light dark:hover:bg-surface2-dark hover:text-ink-light dark:hover:text-ink-dark disabled:opacity-50"
              title="Расставить все доски вертикальной стопкой"
            >
              <AlignVerticalJustifyStart size={14} />
              Авто-лейаут
            </button>
            <button
              type="button"
              onClick={() => setTweaksOpen((v) => !v)}
              className={clsx(
                'inline-flex items-center justify-center w-8 h-8 rounded',
                tweaksOpen
                  ? 'bg-accent/10 text-accent'
                  : 'text-ink-faint_l dark:text-ink-faint_d hover:bg-surface2-light dark:hover:bg-surface2-dark',
              )}
              title="Tweaks"
            >
              <Settings2 size={14} />
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => setShowNewBoardModal(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-accent text-white hover:bg-accent/90"
        >
          <Plus size={14} />
          Доска
        </button>

        {showNewBoardModal && (
          <NewBoardModal
            spaceId={spaceId}
            onClose={() => setShowNewBoardModal(false)}
          />
        )}

        {tweaksOpen && view === 'canvas' && (
          <TweaksPanel
            background={bgKey}
            onSelectBg={(bg) => updateTweaks.mutate({ spaceId, background: bg })}
            onClose={() => setTweaksOpen(false)}
          />
        )}
      </header>

      {view === 'canvas' ? (
        <CanvasArea
          spaceId={spaceId}
          boards={boards as BoardRow[]}
          zoom={zoom}
          bgKey={bgKey}
          onMove={(boardId, x, y) => {
            // Optimistic update — пишем в react-query cache синхронно.
            // Никаких invalidate после mutation: оптимистика уже эквивалентна тому что
            // вернёт сервер. Перезаписать данные сервер может только если позиция была
            // отвергнута (collision detection — пока не реализована).
            utils.spaces.getWithBoards.setData({ spaceId }, (old) => {
              if (!old) return old;
              return {
                ...old,
                boards: old.boards.map((b) =>
                  b.id === boardId ? { ...b, canvasX: x, canvasY: y } : b,
                ),
              };
            });
            updatePos.mutate({ id: boardId, canvasX: x, canvasY: y });
          }}
        />
      ) : (
        <SpaceListView boards={boards as BoardRow[]} />
      )}
    </div>
  );
}

function TweaksPanel({
  background,
  onSelectBg,
  onClose,
}: {
  background: string;
  onSelectBg: (bg: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute right-6 top-14 z-40 w-[260px] rounded-lg shadow-pop border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark p-3 animate-pop">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mb-2">
          Фон канваса
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(BACKGROUNDS).map(([key, bg]) => {
            const active = key === background;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelectBg(key)}
                className={clsx(
                  'aspect-video rounded-md ring-1 transition relative overflow-hidden',
                  active
                    ? 'ring-accent ring-2'
                    : 'ring-line-light dark:ring-line-dark hover:ring-accent/50',
                )}
                style={{ background: bg.light }}
                title={bg.label}
              >
                <span className="absolute bottom-1 left-1 text-[9px] font-medium text-ink-light dark:text-ink-dark bg-white/80 dark:bg-black/60 px-1 rounded-sm">
                  {bg.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function CanvasArea({
  boards,
  zoom,
  bgKey,
  onMove,
}: {
  spaceId: string;
  boards: BoardRow[];
  zoom: number;
  bgKey: string;
  onMove: (boardId: string, x: number, y: number) => void;
}) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<{
    boardId: string;
    startX: number;
    startY: number;
    startCanvasX: number;
    startCanvasY: number;
  } | null>(null);
  const [localPositions, setLocalPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState<{ startX: number; startY: number } | null>(null);
  const [spaceHeld, setSpaceHeld] = useState(false);

  // Refs для актуальных значений внутри постоянных listener'ов.
  // Это убирает re-attach event listeners на каждый mousemove (#H6 из аудита) — тот самый
  // механизм был причиной "дрожания": на каждое движение мыши useEffect перезапускался,
  // создавал новые listeners, между detach/attach терялись события.
  const dragStateRef = useRef(dragState);
  const panningRef = useRef(panning);
  const localPositionsRef = useRef(localPositions);
  const zoomRef = useRef(zoom);
  const onMoveRef = useRef(onMove);
  const boardsRef = useRef(boards);
  const collisionRef = useRef(false);
  const isCollidingRef = useRef(false);
  const [isColliding, setIsColliding] = useState(false);
  useEffect(() => { dragStateRef.current = dragState; }, [dragState]);
  useEffect(() => { panningRef.current = panning; }, [panning]);
  useEffect(() => { localPositionsRef.current = localPositions; }, [localPositions]);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { onMoveRef.current = onMove; }, [onMove]);
  useEffect(() => { boardsRef.current = boards; }, [boards]);

  // Размер доски в cells = canvasW/canvasH из БД (persisted source of truth).
  // DOM-измерение через ResizeObserver было удалено 2026-05-02: создавало
  // нестабильность — drag разрешал позиции которые становились overlap при
  // expand доски (свернутая видна как ~32 cells, развёрнутая 50). Теперь
  // collision и auto-layout одинаково используют canvasH из БД.
  const getBoardSizeCells = (w: number, h: number) => {
    return { w: Math.max(1, w), h: Math.max(1, h) };
  };

  // AABB collision check
  function rectsOverlap(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
  }

  // findNearestFree был удалён 2026-05-02 — snap-to-nearest вернём
  // отдельной фазой когда стабилизируем drag-логику.

  // Space — нажатый = режим pan. Esc — отменить активный drag доски.
  // Также blur/visibilitychange — очистить sticky drag-state если пользователь
  // alt-tab'нул и mouseup не дошёл (раньше доска оставалась в state isDragging).
  useEffect(() => {
    const cancelDrag = () => {
      const ds = dragStateRef.current;
      if (!ds) return;
      // Откат visual position на старт
      setLocalPositions((prev) => {
        const next = new Map(prev);
        next.delete(ds.boardId);
        return next;
      });
      collisionRef.current = false;
      isCollidingRef.current = false;
      setIsColliding(false);
      setDragState(null);
      logAction('board.drag_cancel', { entity: 'board', entityId: ds.boardId });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const isTextInput =
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA' ||
        (document.activeElement as HTMLElement | null)?.isContentEditable;
      if (e.code === 'Space' && !e.repeat && !isTextInput) {
        setSpaceHeld(true);
        e.preventDefault();
      }
      if (e.code === 'Escape' && dragStateRef.current) {
        cancelDrag();
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    const onBlur = () => {
      cancelDrag();
      setSpaceHeld(false);
      setPanning(null);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onBlur();
    });
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Один раз привязываем mousemove/mouseup к window. Listeners читают через refs,
  // поэтому никаких re-attach при изменении state нет.
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      const ds = dragStateRef.current;
      const ps = panningRef.current;
      const z = zoomRef.current;
      if (ds) {
        const dx = (e.clientX - ds.startX) / z;
        const dy = (e.clientY - ds.startY) / z;
        // Размеры драгающей доски — нужны для collision detection и для
        // clamp'а в canvas-bounds (чтобы доска не уехала за 5000px край).
        const draggingBoard = boardsRef.current.find((b) => b.id === ds.boardId);
        const others = boardsRef.current.filter((b) => b.id !== ds.boardId);
        const draggingSize = getBoardSizeCells(
          draggingBoard?.canvasW ?? 80,
          draggingBoard?.canvasH ?? 50,
        );
        // Минимум 1 cell отступ от верха/левого края canvas — чтобы доска
        // не упиралась вплотную в шапку пространства или меню.
        const minPos = 1;
        const maxX = Math.max(minPos, CANVAS_SIZE_CELLS - draggingSize.w);
        const maxY = Math.max(minPos, CANVAS_SIZE_CELLS - draggingSize.h);
        const newX = Math.min(maxX, Math.max(minPos, Math.round((ds.startCanvasX * CELL + dx) / CELL)));
        const newY = Math.min(maxY, Math.max(minPos, Math.round((ds.startCanvasY * CELL + dy) / CELL)));
        const candidate = { x: newX, y: newY, w: draggingSize.w, h: draggingSize.h };
        const collides = others.some((b) => {
          const size = getBoardSizeCells(b.canvasW, b.canvasH);
          return rectsOverlap(candidate, {
            x: b.canvasX,
            y: b.canvasY,
            w: size.w,
            h: size.h,
          });
        });

        setLocalPositions((prev) => {
          const next = new Map(prev);
          next.set(ds.boardId, { x: newX, y: newY });
          return next;
        });
        // Сохраняем флаг коллизии — отображается красным ring и блокирует drop
        collisionRef.current = collides;
        if (collides !== isCollidingRef.current) {
          isCollidingRef.current = collides;
          setIsColliding(collides);
        }
      }
      if (ps) {
        const dx = e.clientX - ps.startX;
        const dy = e.clientY - ps.startY;
        setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
        setPanning({ startX: e.clientX, startY: e.clientY });
      }
    };
    const handleUp = () => {
      const ds = dragStateRef.current;
      if (ds) {
        const local = localPositionsRef.current.get(ds.boardId);
        const colliding = collisionRef.current;
        // Helper: clear local после save — иначе local имеет приоритет в render
        // (`y = local?.y ?? b.canvasY`) и доска визуально остаётся в drop-месте
        // пока user снова её не дёрнет. Удаляем сразу после onMove чтобы
        // render использовал свежий optimistic-update'ный canvasY.
        const clearLocal = (id: string) => {
          setLocalPositions((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
        };
        if (local && !colliding) {
          // Свободно — закрепляем там, куда отпустили.
          onMoveRef.current(ds.boardId, local.x, local.y);
          clearLocal(ds.boardId);
          logAction('board.drag_end', {
            entity: 'board',
            entityId: ds.boardId,
            payload: {
              from: { x: ds.startCanvasX, y: ds.startCanvasY },
              to: local,
            },
          });
        } else if (colliding && local) {
          // Занято — простой snap-back на старт. Snap-to-nearest отключён
          // 2026-05-02 (создавал ощущение «доска уехала непонятно куда»
          // плюс race-conditions с boardsRef). Вернём в отдельной фазе.
          clearLocal(ds.boardId);
          logAction('board.drag_collision', {
            entity: 'board',
            entityId: ds.boardId,
            payload: { from: { x: ds.startCanvasX, y: ds.startCanvasY } },
          });
        } else {
          logAction('board.drag_cancel', { entity: 'board', entityId: ds.boardId });
        }
      }
      collisionRef.current = false;
      isCollidingRef.current = false;
      setIsColliding(false);
      setDragState(null);
      setPanning(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []); // ← пустой deps array, listeners привязаны один раз

  const handlePanStart = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.button === 1 || (e.button === 0 && spaceHeld)) {
      setPanning({ startX: e.clientX, startY: e.clientY });
      e.preventDefault();
    }
  };

  const bg = BACKGROUNDS[bgKey] ?? BACKGROUNDS['cool-mist'];
  const bgValue = theme === 'dark' ? bg.dark : bg.light;

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex-1 overflow-auto relative',
        spaceHeld ? 'cursor-grab' : 'cursor-default',
        panning && 'cursor-grabbing',
      )}
      onMouseDown={handlePanStart}
      style={{ background: bgValue }}
    >
      <div
        className="relative"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: '5000px',
          height: '5000px',
          // Сетка видна только при drag доски
          backgroundImage: dragState
            ? `linear-gradient(to right, rgba(91,91,214,0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(91,91,214,0.10) 1px, transparent 1px)`
            : undefined,
          backgroundSize: dragState ? `${CELL}px ${CELL}px` : undefined,
        }}
      >
        {boards.length === 0 ? (
          <div
            className="absolute text-center text-ink-faint_l dark:text-ink-faint_d text-sm pointer-events-none"
            style={{ left: 40, top: 40 }}
          >
            <p>В пространстве пока нет досок.</p>
            <p className="text-xs mt-1">Нажми «Доска» сверху.</p>
          </div>
        ) : (
          boards.map((b) => {
            const local = localPositions.get(b.id);
            const isDragging = dragState?.boardId === b.id;
            const x = (local?.x ?? b.canvasX) * CELL;
            const y = (local?.y ?? b.canvasY) * CELL;
            const showCollision = isDragging && isColliding;
            return (
              <div
                key={b.id}
                className={clsx(
                  'group/board',
                  showCollision && 'ring-2 ring-red-500 rounded-lg',
                )}
                style={{
                  position: 'absolute',
                  left: x,
                  top: y,
                  zIndex: isDragging ? 100 : 1,
                  // Плавный «доезд» на место после snap-to-nearest и отскок при
                  // collapse-reflow соседей. При активном drag — без transition,
                  // иначе доска лагает за курсором.
                  transition: isDragging
                    ? 'none'
                    : 'left var(--dur-med) var(--ease-out), top var(--dur-med) var(--ease-out)',
                }}
              >
                <BoardWrapper
                  boardId={b.id}
                  onDragStart={(e) => {
                    setDragState({
                      boardId: b.id,
                      startX: e.clientX,
                      startY: e.clientY,
                      startCanvasX: b.canvasX,
                      startCanvasY: b.canvasY,
                    });
                    logAction('board.drag_start', {
                      entity: 'board',
                      entityId: b.id,
                      payload: { x: b.canvasX, y: b.canvasY },
                    });
                  }}
                >
                  <Board board={b} isDragging={isDragging} />
                </BoardWrapper>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function BoardWrapper({
  children,
  onDragStart,
}: {
  boardId: string;
  onDragStart: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        const handle = target.closest('[data-board-header="1"]');
        if (handle && e.button === 0) {
          if (target.closest('button')) return;
          onDragStart(e);
          e.preventDefault();
        }
      }}
    >
      {children}
    </div>
  );
}
